import React, {Component, type ErrorInfo, type ReactNode, useState} from "react";
import Markdown from "react-markdown";
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import remarkGfm from "remark-gfm";


import {buildChartFromBuilder} from "@components/charts/echarts-toolkit";
import {EChartsCard} from "@components/charts/EChartsCard";
import {Badge} from "@components/ui/badge";
import {formatCurrency} from "@lib/format";

import {deltaColor, formatDelta, highlightClass, parseNumericValue, statusToBadgeClasses, statusToVariant} from "./report-helpers";

import type {
    BridgeTableBlock,
    CalloutBlock,
    ChartBlock,
    ChartSeries,
    ComparisonTableBlock,
    DataTableBlock,
    KpiRowBlock,
    KeyValueListBlock,
    MatrixGridBlock,
    MetricGridBlock,
    MetricGridItem,
    NarrativeBlock,
    RechartsChartBlock,
    ReportBlock,
    SectionHeaderBlock,
    StatusBadgeBlock,
    StatusListBlock,
    VisualReport,
} from "./visual-report";
import type {EChartsOption} from "echarts";

// ── Sub-renderers ────────────────────────────────────────────────

function StatusBadges({block}: {block: StatusBadgeBlock}) {
    const variantMap: Record<string, "secondary" | "outline" | "destructive"> = {
        success: "secondary",
        warning: "outline",
        danger: "destructive",
    };
    const colorMap: Record<string, string> = {
        success: "bg-green-100 text-green-800",
        warning: "border-amber-300 text-amber-700",
        danger: "",
    };
    const badges = Array.isArray(block.badges) ? block.badges : [];
    if (badges.length === 0) {
        return <p className="text-xs text-gray-400 py-2">No status badges available</p>;
    }
    return (
        <div className="flex items-center gap-2 flex-wrap">
            {badges.map((b, i) => (
                <Badge
                    key={i}
                    variant={variantMap[b.variant] ?? "secondary"}
                    className={`text-[10px] px-1.5 py-0 ${colorMap[b.variant] ?? ""}`}
                >
                    {b.count} {b.label}
                </Badge>
            ))}
        </div>
    );
}

function StatusList({block}: {block: StatusListBlock}) {
    const [showAll, setShowAll] = useState(false);
    const items = Array.isArray(block.items) ? block.items : [];
    const maxVisible = block.max_visible ?? items.length;
    const visible = showAll ? items : items.slice(0, maxVisible);

    if (items.length === 0) {
        return <p className="text-xs text-gray-400 py-2">No status items available</p>;
    }

    return (
        <div className="space-y-1">
            {visible.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                    <Badge
                        variant={statusToVariant(item.status)}
                        className={`text-[10px] px-1.5 py-0 mt-0.5 shrink-0 ${statusToBadgeClasses(item.status)}`}
                    >
                        {item.status}
                    </Badge>
                    <div className="min-w-0">
                        <span className="font-mono text-[11px]">{item.label}</span>
                        {item.detail && (
                            <p className="text-muted-foreground truncate">{item.detail}</p>
                        )}
                    </div>
                </div>
            ))}
            {items.length > maxVisible && !showAll && (
                <button
                    onClick={() => setShowAll(true)}
                    className="text-xs text-[#209DB3] hover:underline"
                >
                    Show all {items.length} items
                </button>
            )}
        </div>
    );
}

function MetricGrid({block}: {block: MetricGridBlock}) {
    // Defensive: handle missing or mis-keyed items (legacy "metrics" key)
    const raw = block as unknown as Record<string, unknown>;
    const items: MetricGridItem[] = Array.isArray(block.items)
        ? block.items
        : Array.isArray(raw["metrics"])
          ? (raw["metrics"] as MetricGridItem[])
          : [];
    if (items.length === 0) {
        return <p className="text-xs text-gray-400 py-2">No metrics available</p>;
    }
    const cols = block.columns ?? 2;
    return (
        <div className="grid gap-2" style={{gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`}}>
            {items.map((item, i) => (
                <div key={i} className="text-xs">
                    <p className="text-[#64748b] font-medium">{item.label}</p>
                    <p className="text-[#1e293b] font-semibold">{item.value}</p>
                    {item.detail && (
                        <p className="text-[10px] text-[#64748b]">{item.detail}</p>
                    )}
                </div>
            ))}
        </div>
    );
}

function ComparisonTable({block}: {block: ComparisonTableBlock}) {
    const columns = Array.isArray(block.columns) ? block.columns : [];
    const rows = Array.isArray(block.rows) ? block.rows : [];
    if (columns.length === 0) {
        return <p className="text-xs text-gray-400 py-2">No table columns available</p>;
    }
    return (
        <div>
            {block.caption && (
                <p className="text-xs text-[#64748b] mb-1">{block.caption}</p>
            )}
            <table className="w-full text-xs">
                <thead>
                    <tr className="border-b border-gray-200">
                        {columns.map(col => (
                            <th
                                key={col.key}
                                className={`py-1 text-[#64748b] font-medium ${col.align === "right" ? "text-right" : "text-left"}`}
                            >
                                {col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className="border-b border-gray-50">
                            {columns.map((col, ci) => {
                                const val = row.values[col.key] ?? "";
                                const isLastCol = ci === columns.length - 1;
                                const colorCls = isLastCol && row.highlight ? highlightClass(row.highlight) : "";
                                return (
                                    <td
                                        key={col.key}
                                        className={`py-1.5 ${col.align === "right" ? "text-right" : ""} ${
                                            ci === 0 ? "font-medium text-[#1e293b]" : isLastCol && colorCls ? `font-medium ${colorCls}` : col.align === "right" ? "text-[#64748b]" : "text-[#1e293b]"
                                        }`}
                                    >
                                        {isLastCol && row.highlight ? formatDelta(val) : val}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function KeyValueList({block}: {block: KeyValueListBlock}) {
    const items = Array.isArray(block.items) ? block.items : [];
    if (items.length === 0) {
        return <p className="text-xs text-gray-400 py-2">No key values available</p>;
    }
    return (
        <div>
            {block.heading && (
                <p className="text-xs text-[#64748b] font-medium mb-1">{block.heading}</p>
            )}
            <div className="space-y-0.5">
                {items.map((item, i) => {
                    const parsed = parseNumericValue(item.value);
                    const delta = parsed ?? 0;
                    const colorCls = item.highlight ? highlightClass(item.highlight) : deltaColor(delta);
                    return (
                        <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-[#1e293b] truncate max-w-[50%]">{item.label}</span>
                            <span className={`font-medium ${colorCls}`}>
                                {formatDelta(item.value)}
                                {item.detail && (
                                    <span className="text-[#64748b] ml-1">({item.detail})</span>
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function NarrativeRenderer({block}: {block: NarrativeBlock}) {
    const body = typeof block.body === "string" ? block.body : "";
    if (!body) {
        return <p className="text-xs text-gray-400 py-2">No narrative content available</p>;
    }
    return (
        <div className="prose prose-sm max-w-none text-[#334155] prose-headings:text-[#1e293b] prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-li:my-0 prose-ul:my-1 prose-ol:my-1 prose-strong:text-[#1e293b]">
            <Markdown remarkPlugins={[remarkGfm]}>{body}</Markdown>
        </div>
    );
}

// ── New block renderers ──────────────────────────────────────────

function KpiRow({block}: {block: KpiRowBlock}) {
    const items = Array.isArray(block.items) ? block.items : [];
    if (items.length === 0) return null;
    const subColorMap: Record<string, string> = {
        red: "text-red-500",
        green: "text-green-600",
        amber: "text-amber-600",
        neutral: "text-[#64748b]",
    };
    return (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
            {items.map((item, i) => (
                <div
                    key={i}
                    className={`rounded-xl border border-gray-200 bg-white p-4${item.highlight ? " border-l-4 border-l-amber-500" : ""}`}
                >
                    <p className="text-[10px] uppercase tracking-wide text-[#64748b]">{item.label}</p>
                    <p className="text-xl font-bold text-[#1e293b]">{item.value}</p>
                    {item.sub_text && (
                        <p className={`text-xs mt-1 ${subColorMap[item.sub_color ?? "neutral"] ?? "text-[#64748b]"}`}>
                            {item.sub_text}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

function SectionHeader({block}: {block: SectionHeaderBlock}) {
    return (
        <div className="border-b-2 border-[#209DB3] pb-2 mb-2">
            <h5 className="text-base font-bold text-[#1e293b]">{block.title}</h5>
            {block.subtitle && (
                <p className="text-xs text-[#64748b]">{block.subtitle}</p>
            )}
        </div>
    );
}

function BridgeTable({block}: {block: BridgeTableBlock}) {
    const columns = Array.isArray(block.columns) ? block.columns : [];
    const rows = Array.isArray(block.rows) ? block.rows : [];
    if (columns.length === 0) return null;

    const highlightCls = (h?: string) => {
        switch (h) {
            case "favourable": return "text-green-600 font-semibold";
            case "unfavourable": return "text-red-500 font-semibold";
            case "flagged": return "text-amber-600 font-semibold";
            default: return "";
        }
    };

    const rowBg = (rt?: string) => {
        switch (rt) {
            case "section_header": return "bg-gray-100 font-bold";
            case "subtotal": return "bg-gray-50 font-semibold";
            case "total": return "bg-gray-200 font-bold";
            default: return "";
        }
    };

    return (
        <div>
            {block.caption && <p className="text-xs text-[#64748b] mb-1">{block.caption}</p>}
            <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-[#209DB3] text-white sticky top-0">
                            {columns.map(col => (
                                <th key={col.key} className={`py-2 px-3 ${col.align === "right" ? "text-right" : "text-left"}`}>
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => {
                            if (row.row_type === "section_header") {
                                const label = row.values[columns[0]?.key] ?? "";
                                return (
                                    <tr key={i} className="bg-gray-100">
                                        <td colSpan={columns.length} className="py-2 px-3 font-bold text-[#1e293b]">
                                            {label}
                                        </td>
                                    </tr>
                                );
                            }
                            return (
                                <tr key={i} className={`border-b border-gray-100 hover:bg-gray-50 ${rowBg(row.row_type)}`}>
                                    {columns.map((col, ci) => {
                                        const val = row.values[col.key] ?? "";
                                        const isNumCol = col.align === "right";
                                        const colorCls = isNumCol && row.highlight ? highlightCls(row.highlight) : "";
                                        return (
                                            <td
                                                key={col.key}
                                                className={`py-1.5 px-3 ${isNumCol ? "text-right tabular-nums" : ""} ${ci === 0 ? "text-[#1e293b]" : ""} ${colorCls}`}
                                            >
                                                {val}
                                                {ci === columns.length - 1 && row.commentary && (
                                                    <p className="text-xs text-gray-500 max-w-[360px] mt-0.5">{row.commentary}</p>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const CALLOUT_STYLES: Record<string, string> = {
    warning: "bg-amber-50 border-amber-200 border-l-amber-500",
    critical: "bg-red-50 border-red-200 border-l-red-500",
    info: "bg-teal-50 border-teal-200 border-l-[#209DB3]",
    success: "bg-green-50 border-green-200 border-l-green-500",
};

function Callout({block}: {block: CalloutBlock}) {
    const style = CALLOUT_STYLES[block.variant] ?? CALLOUT_STYLES.info;
    return (
        <div className={`rounded-lg border border-l-4 p-4 ${style}`}>
            <p className="text-sm font-semibold text-[#1e293b]">{block.title}</p>
            <p className="text-xs text-[#334155] mt-1">{block.body}</p>
            {block.detail && (
                <p className="text-[10px] text-[#64748b] mt-2">{block.detail}</p>
            )}
        </div>
    );
}

function DataTable({block}: {block: DataTableBlock}) {
    const columns = Array.isArray(block.columns) ? block.columns : [];
    const allRows = Array.isArray(block.rows) ? block.rows : [];
    const [filter, setFilter] = useState<string>("");
    const [sortKey, setSortKey] = useState<string>("");
    const [sortAsc, setSortAsc] = useState(true);

    if (columns.length === 0) return null;

    const filteredRows = filter && block.filter_key
        ? allRows.filter(r => r.group === filter)
        : allRows;

    const sortable = block.sortable !== false;
    const sortedRows = sortKey && sortable
        ? [...filteredRows].sort((a, b) => {
            const av = a.values[sortKey] ?? "";
            const bv = b.values[sortKey] ?? "";
            const an = typeof av === "number" ? av : parseFloat(String(av).replace(/[$,%]/g, ""));
            const bn = typeof bv === "number" ? bv : parseFloat(String(bv).replace(/[$,%]/g, ""));
            if (!isNaN(an) && !isNaN(bn)) return sortAsc ? an - bn : bn - an;
            return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
        })
        : filteredRows;

    const groups = block.filter_key
        ? [...new Set(allRows.map(r => r.group).filter(Boolean))]
        : [];

    const maxH = block.max_height ?? 400;

    const handleSort = (key: string) => {
        if (!sortable) return;
        if (sortKey === key) { setSortAsc(!sortAsc); }
        else { setSortKey(key); setSortAsc(true); }
    };

    return (
        <div>
            {block.caption && <p className="text-xs text-[#64748b] mb-1">{block.caption}</p>}
            {groups.length > 0 && (
                <select
                    className="text-xs border rounded px-2 py-1 mb-2"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                >
                    <option value="">All</option>
                    {groups.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
            )}
            <div className="overflow-y-auto rounded-lg border" style={{maxHeight: maxH}}>
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-[#0F172A] text-white sticky top-0">
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    className={`py-2 px-3 ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"} ${sortable ? "cursor-pointer select-none" : ""}`}
                                    onClick={() => handleSort(col.key)}
                                >
                                    {col.label}
                                    {sortKey === col.key && (sortAsc ? " ▲" : " ▼")}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedRows.map((row, i) => (
                            <tr key={i} className={`border-b border-gray-100 hover:bg-gray-50 ${row.highlight_row ? "bg-amber-50 font-semibold" : ""}`}>
                                {columns.map(col => {
                                    const val = row.values[col.key] ?? "";
                                    return (
                                        <td
                                            key={col.key}
                                            className={`py-1.5 px-3 ${col.align === "right" ? "text-right tabular-nums" : col.align === "center" ? "text-center" : ""}`}
                                        >
                                            {val}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const MATRIX_COLOR_MAP: Record<string, string> = {
    green: "34, 197, 94",
    amber: "245, 158, 11",
    red: "239, 68, 68",
    blue: "32, 157, 179",
};

function MatrixGrid({block}: {block: MatrixGridBlock}) {
    const rowHeaders = Array.isArray(block.row_headers) ? block.row_headers : [];
    const colHeaders = Array.isArray(block.col_headers) ? block.col_headers : [];
    const cells = Array.isArray(block.cells) ? block.cells : [];
    if (rowHeaders.length === 0 || colHeaders.length === 0) return null;

    return (
        <div>
            {block.title && <p className="text-sm font-semibold text-[#1e293b] mb-1">{block.title}</p>}
            {block.description && <p className="text-[10px] text-[#64748b] mb-2">{block.description}</p>}
            <div className="overflow-x-auto">
                <div
                    className="grid text-[11px]"
                    style={{gridTemplateColumns: `120px repeat(${colHeaders.length}, minmax(60px, 1fr))`}}
                >
                    {/* Top-left corner */}
                    <div className="bg-[#209DB3] p-2" />
                    {/* Column headers */}
                    {colHeaders.map((ch, i) => (
                        <div key={i} className="bg-[#209DB3] text-white font-semibold text-center p-2 text-[10px]">
                            {ch}
                        </div>
                    ))}
                    {/* Rows */}
                    {rowHeaders.map((rh, ri) => (
                        <React.Fragment key={`row-${ri}`}>
                            <div className="bg-[#209DB3] text-white font-semibold text-left p-2">
                                {rh}
                            </div>
                            {colHeaders.map((_, ci) => {
                                const cell = cells[ri]?.[ci];
                                if (!cell) {
                                    return (
                                        <div key={`${ri}-${ci}`} className="bg-gray-100 text-gray-400 text-center p-2">
                                            —
                                        </div>
                                    );
                                }
                                const rgb = MATRIX_COLOR_MAP[cell.color ?? "blue"] ?? MATRIX_COLOR_MAP.blue;
                                const intensity = cell.intensity ?? 0.5;
                                return (
                                    <div
                                        key={`${ri}-${ci}`}
                                        className="text-center p-2"
                                        style={{backgroundColor: `rgba(${rgb}, ${(intensity * 0.4 + 0.05).toFixed(2)})`}}
                                    >
                                        <span className="font-semibold">{cell.value}</span>
                                        {cell.detail && (
                                            <span className="block text-[9px] opacity-90">{cell.detail}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}

const DEFAULT_COLORS = [
    "#5BF6C8", "#209DB3", "#6366f1", "#f59e0b", "#ef4444",
    "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#64748b",
];

function formatYTick(value: number, yFormat?: string): string {
    if (yFormat === "currency") return formatCurrency(value, true);
    if (yFormat === "percent") return `${value}%`;
    if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
    return String(value);
}

function ChartRenderer({block}: {block: ChartBlock}) {
    const height = block.height ?? 220;

    // ECharts builder dispatch — uses toolkit builder functions
    if (block.chart_engine === "echarts_builder") {
        const option = buildChartFromBuilder(block.builder, block.builder_config);
        if (!option) return <p className="text-xs text-gray-400 py-2">Unknown chart builder: {block.builder}</p>;
        return (
            <div data-testid="chart-block-echarts">
                <EChartsCard option={option} height={height} />
            </div>
        );
    }

    // ECharts path — requires echarts_option
    if (block.chart_engine === "echarts") {
        return (
            <div data-testid="chart-block-echarts">
                <EChartsCard
                    option={block.echarts_option as EChartsOption}
                    height={height}
                />
            </div>
        );
    }

    // Normalise legacy camelCase keys produced by older sandbox tools
    const rechartsBlock = block as RechartsChartBlock & Record<string, unknown>;
    const xKey = rechartsBlock.x_key ?? (rechartsBlock as Record<string, unknown>)["xKey"] as string ?? "name";
    const chartType = rechartsBlock.chart_type ?? (rechartsBlock as Record<string, unknown>)["chartType"] as string ?? "bar";
    const rawSeries = rechartsBlock.series
        ?? ((rechartsBlock as Record<string, unknown>)["dataKeys"] as string[] | undefined)?.map((k: string) => ({key: k, label: k.replace(/_/g, " ")}));
    if (!rawSeries || rawSeries.length === 0) {
        return <p className="text-xs text-gray-400 py-2">Chart data unavailable (no series defined)</p>;
    }

    // Existing Recharts path
    return (
        <div style={{width: "100%", height}} data-testid="chart-block">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={rechartsBlock.data} margin={{top: 4, right: 8, left: 0, bottom: 0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                        dataKey={xKey}
                        tick={{fontSize: 10, fill: "#64748b"}}
                        axisLine={{stroke: "#e2e8f0"}}
                        tickLine={false}
                    />
                    <YAxis
                        tick={{fontSize: 10, fill: "#64748b"}}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v: number) => formatYTick(v, rechartsBlock.y_format)}
                        width={56}
                    />
                    <Tooltip
                        contentStyle={{fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0"}}
                        formatter={(value: number) =>
                            rechartsBlock.y_format === "currency"
                                ? formatCurrency(value, true)
                                : rechartsBlock.y_format === "percent"
                                  ? `${value}%`
                                  : value.toLocaleString()
                        }
                    />
                    {rechartsBlock.show_legend !== false && (
                        <Legend
                            wrapperStyle={{fontSize: 10}}
                            iconType="plainline"
                            iconSize={10}
                        />
                    )}
                    {rechartsBlock.forecast_boundary && (
                        <ReferenceLine
                            x={rechartsBlock.forecast_boundary}
                            stroke="#94a3b8"
                            strokeDasharray="4 4"
                            label={{value: "Forecast →", position: "top", fontSize: 9, fill: "#94a3b8"}}
                        />
                    )}
                    {rawSeries.map((s: ChartSeries, i: number) => {
                        const color = s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length];
                        const seriesType = s.type ?? (chartType === "bar" ? "bar" : "line");
                        if (seriesType === "bar") {
                            return (
                                <Bar
                                    key={s.key}
                                    dataKey={s.key}
                                    name={s.label}
                                    fill={color}
                                    radius={s.stack_id ? [0, 0, 0, 0] : [2, 2, 0, 0]}
                                    maxBarSize={32}
                                    stackId={s.stack_id}
                                />
                            );
                        }
                        return (
                            <Line
                                key={s.key}
                                dataKey={s.key}
                                name={s.label}
                                stroke={color}
                                strokeWidth={2}
                                dot={false}
                                type="monotone"
                                strokeDasharray={s.dash ? "6 3" : undefined}
                            />
                        );
                    })}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

// ── Block dispatcher ─────────────────────────────────────────────

function BlockRenderer({block}: {block: ReportBlock}) {
    switch (block.type) {
        case "status_badges":
            return <StatusBadges block={block} />;
        case "status_list":
            return <StatusList block={block} />;
        case "metric_grid":
            return <MetricGrid block={block} />;
        case "comparison_table":
            return <ComparisonTable block={block} />;
        case "kv_list":
            return <KeyValueList block={block} />;
        case "narrative":
            return <NarrativeRenderer block={block} />;
        case "chart":
            return <ChartRenderer block={block} />;
        case "kpi_row":
            return <KpiRow block={block} />;
        case "section_header":
            return <SectionHeader block={block} />;
        case "bridge_table":
            return <BridgeTable block={block} />;
        case "callout":
            return <Callout block={block} />;
        case "data_table":
            return <DataTable block={block} />;
        case "matrix_grid":
            return <MatrixGrid block={block} />;
        default:
            return <p className="text-xs text-gray-400 py-1">Unsupported block type: {(block as Record<string, unknown>).type as string}</p>;
    }
}

// ── Error boundary for individual blocks ─────────────────────────

class BlockErrorBoundary extends Component<
    {children: ReactNode; blockType: string},
    {hasError: boolean; error: string}
> {
    state = {hasError: false, error: ""};

    static getDerivedStateFromError(error: Error) {
        return {hasError: true, error: error.message};
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("Block render error:", error, info);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-600">
                    <p className="font-medium">Failed to render {this.props.blockType} block</p>
                    <p className="text-[10px] text-red-400 mt-1">{this.state.error}</p>
                </div>
            );
        }
        return this.props.children;
    }
}

// ── Main component ───────────────────────────────────────────────

export function GenericReportCard({report}: {report: VisualReport}) {
    return (
        <div className="rounded-lg border-2 border-[#5BF6C8] bg-white p-4 my-2 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-sm font-semibold text-[#1e293b]">{report.title}</h4>
                {report.subtitle && (
                    <span className="text-xs text-[#64748b]">{report.subtitle}</span>
                )}
            </div>
            {report.blocks.map((block, i) => (
                <BlockErrorBoundary key={i} blockType={block.type}>
                    <BlockRenderer block={block} />
                </BlockErrorBoundary>
            ))}
        </div>
    );
}
